import { useState } from 'react'
import { ArrowLeft, ChevronDown, Check } from 'lucide-react'
import { resolvePlayerTeeRatings } from '../../utils/handicaps'

/**
 * Mixed Tee sub-screen: shows all players with a tee dropdown styled to match CourseSearch tee picker.
 * Shared between RoundSetupWizard and SessionSetupWizard.
 */
export default function MixedTeeSetup({ matchPlayers, selectedTee, courseTees, mixedTees, onUpdate, onBack }) {
  const [openPlayerId, setOpenPlayerId] = useState(null)

  const handleTeeSelect = (playerId, tee) => {
    onUpdate({ ...mixedTees, [playerId]: tee })
    setOpenPlayerId(null)
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
        <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', padding: '0.25rem', color: 'var(--text-primary)', cursor: 'pointer' }}>
          <ArrowLeft size={20} />
        </button>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Mixed Tees</h3>
      </div>

      <div style={{
        padding: '0.75rem',
        backgroundColor: 'var(--bg-tertiary)',
        borderRadius: '0.5rem',
        marginBottom: '1rem',
        fontSize: '0.8125rem',
        color: 'var(--text-secondary)',
      }}>
        Primary tee: <strong style={{ color: 'var(--text-primary)' }}>{selectedTee.name}</strong>
        {selectedTee.rating && selectedTee.slope && (
          <span> ({selectedTee.rating} / {selectedTee.slope})</span>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {matchPlayers.map((mp) => {
          const player = mp.player || {}
          const playerName = player.display_name || player.first_name || 'Player'
          const currentTee = mixedTees[mp.player_id] || selectedTee
          const isOpen = openPlayerId === mp.player_id
          const isAlternate = currentTee.name !== selectedTee.name
          const playerGender = mp.gender || 'M'
          const currentRatings = resolvePlayerTeeRatings(currentTee, playerGender)

          return (
            <div key={mp.player_id}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500' }}>
                {playerName}
              </label>
              <button
                type="button"
                onClick={() => setOpenPlayerId(isOpen ? null : mp.player_id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '0.625rem 0.75rem',
                  borderRadius: '0.5rem',
                  backgroundColor: isAlternate ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.06)' : 'var(--bg-tertiary)',
                  border: `1px solid ${isAlternate ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <div>
                  <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>{currentTee.name}</span>
                  {currentTee.totalYardage > 0 && (
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginLeft: '0.5rem' }}>
                      {currentTee.totalYardage.toLocaleString()} yds
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {currentRatings.rating && currentRatings.slope && (
                    <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                      {currentRatings.rating} / {currentRatings.slope}
                    </span>
                  )}
                  <ChevronDown size={16} style={{
                    color: 'var(--text-muted)',
                    transition: 'transform 0.2s',
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }} />
                </div>
              </button>
              {isOpen && (
                <div style={{
                  marginTop: '0.25rem',
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                }}>
                  {courseTees.map((tee) => {
                    const isSelected = currentTee.name === tee.name
                    const teeRatings = resolvePlayerTeeRatings(tee, playerGender)
                    return (
                      <button
                        key={tee.name}
                        type="button"
                        onClick={() => handleTeeSelect(mp.player_id, tee)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          padding: '0.625rem 0.75rem',
                          textAlign: 'left',
                          borderBottom: '1px solid var(--border-color)',
                          backgroundColor: isSelected ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)' : 'transparent',
                          borderRadius: '0',
                        }}
                      >
                        <div>
                          <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>{tee.name}</span>
                          {tee.totalYardage > 0 && (
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginLeft: '0.5rem' }}>
                              {tee.totalYardage.toLocaleString()} yds
                            </span>
                          )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          {teeRatings.rating && teeRatings.slope ? (
                            <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                              {teeRatings.rating} / {teeRatings.slope}
                            </span>
                          ) : (
                            <span style={{ fontSize: '0.75rem', color: 'var(--accent-secondary, #f59e0b)' }}>
                              Enter rating/slope
                            </span>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <button
        type="button"
        className="btn-primary"
        onClick={onBack}
        style={{ width: '100%', marginTop: '1rem', padding: '0.75rem' }}
      >
        Done
      </button>
    </div>
  )
}
